import React from "react";
import {Modal} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ConsultaModal} from "./ConsultaModal";
import {TIPO_CONVERSACION} from "../../../_helpers";

/**
 * Lo que tiene que saber alguien que llega por primera vez, en una sola franja.
 *
 * <p>Reemplaza al carrusel del banner: una foto con "DEAUTOPARTES" escrito adentro, un segundo
 * titulo encima y dos slides que rotaban solos. Ocupaba el lugar mas valioso de la home para decir
 * poco. Aca van los motivos para comprar, cada uno con su icono, y el contacto para quien quiere
 * vender —que era el segundo slide—.
 *
 * <p>Solo afirma cosas que el sitio efectivamente hace: si se agrega o se saca una funcion
 * (alertas, pago por MercadoPago, consulta por WhatsApp), esta lista tiene que acompañar.
 */
const MOTIVOS = [
    {
        icono: "fa-solid fa-store",
        titulo: "Vendedores oficiales",
        detalle: "Autopartistas registrados",
    },
    {
        icono: "fa-solid fa-credit-card",
        titulo: "Pagá con Mercado Pago",
        detalle: "Tarjeta o dinero en cuenta",
    },
    {
        icono: "fa-brands fa-whatsapp",
        titulo: "Consultá por WhatsApp",
        detalle: "Directo con el vendedor",
    },
    {
        icono: "fa-solid fa-bell",
        titulo: "Alertas de repuestos",
        detalle: "Te avisamos cuando entra",
    },
];

/** Tiene que coincidir con el `mq(medium)` de _confianza.scss: debajo de eso la franja es carrusel. */
const CARRUSEL_QUERY = "(max-width: 44.99em)";
const INTERVALO_MS = 3500;
/** Despues de que la persona desliza a mano, cuanto se espera antes de volver a rotar solo. */
const PAUSA_TRAS_TOQUE_MS = 8000;

/**
 * En mobile la lista de motivos es un carrusel de un motivo por vez (scroll-snap, se desliza con
 * el dedo) que ademas avanza solo. Con los cuatro a la vista la franja ocupaba media pantalla antes
 * del primer producto; asi queda en un renglon.
 *
 * <p>Avanzar es scrollear la lista, no cambiar estado: el mismo scroll que hace el dedo, asi que
 * los dos caminos no se pisan y no hace falta sincronizar ningun indice.
 */
const useRotacion = (listaRef) => {
    React.useEffect(() => {
        const lista = listaRef.current;
        if (!lista || typeof window.matchMedia !== "function") return undefined;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

        const mql = window.matchMedia(CARRUSEL_QUERY);
        let pausadoHasta = 0;

        const tocar = () => {
            pausadoHasta = Date.now() + PAUSA_TRAS_TOQUE_MS;
        };

        const avanzar = () => {
            if (!mql.matches || document.hidden || Date.now() < pausadoHasta) return;

            const items = lista.children;
            if (items.length < 2) return;

            // El paso es la distancia real entre dos motivos (ancho + el gap del CSS), no el ancho
            // de la lista: con el gap, `indice * clientWidth` se iba quedando corto.
            const paso = items[1].offsetLeft - items[0].offsetLeft;
            if (!paso) return;

            const actual = Math.round(lista.scrollLeft / paso);
            const siguiente = (actual + 1) % items.length;

            lista.scrollTo({left: siguiente * paso, behavior: "smooth"});
        };

        lista.addEventListener("pointerdown", tocar, {passive: true});
        lista.addEventListener("touchstart", tocar, {passive: true});
        const intervalo = setInterval(avanzar, INTERVALO_MS);

        return () => {
            clearInterval(intervalo);
            lista.removeEventListener("pointerdown", tocar);
            lista.removeEventListener("touchstart", tocar);
        };
    }, [listaRef]);
};

export const FranjaConfianza = () => {
    const [mostrarConsulta, setMostrarConsulta] = React.useState(false);
    const listaRef = React.useRef(null);

    useRotacion(listaRef);

    return (
        <>
            <section className="confianza" aria-label="Por qué comprar en DeAutopartes">
                <ul className="confianza__motivos" ref={listaRef}>
                    {MOTIVOS.map((m) => (
                        <li key={m.titulo} className="confianza__motivo">
                            <span className="confianza__icono" aria-hidden="true">
                                <FontAwesomeIcon icon={m.icono}/>
                            </span>
                            <span className="confianza__texto">
                                <strong>{m.titulo}</strong>
                                <span>{m.detalle}</span>
                            </span>
                        </li>
                    ))}
                </ul>

                <div className="confianza__contacto">
                    <span className="confianza__contacto-pregunta">¿Vendés autopartes?</span>
                    <button
                        type="button"
                        className="button"
                        data-type="primary-small-alternative"
                        title="Contactanos"
                        aria-label="Contactanos"
                        onClick={() => setMostrarConsulta(true)}
                    >
                        <FontAwesomeIcon icon="fa-solid fa-envelope"/>
                        <span>Contactanos</span>
                    </button>
                </div>
            </section>

            <Modal size="md" centered show={mostrarConsulta} onHide={() => setMostrarConsulta(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Contacto</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ConsultaModal
                        handleClose={() => setMostrarConsulta(false)}
                        type={TIPO_CONVERSACION.NUEVO_VENDEDOR}
                    />
                </Modal.Body>
            </Modal>
        </>
    );
};
